import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  message,
  Breadcrumb,
  Switch,
  Select,
  Drawer,
  Form,
  Input,
  InputNumber,
  Checkbox,
  TimePicker,
  Card,
  Descriptions,
  Radio,
  Segmented,
  Tooltip
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  BellOutlined,
  MessageOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment-timezone";

import { WHITE } from "../../constant/colors";
import {
  getRefillReminderConfigurationsAPI,
  createRefillReminderConfigurationAPI,
  updateRefillReminderConfigurationAPI,
  deleteRefillReminderConfigurationAPI,
  getPatientRefillSettingsAPI,
  getBrandJourneyConfigurationsAPI
} from "../../api/api";
import { useGetSetting } from "../../hooks/getSetting";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "every_other_day", label: "Every Other Day" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom interval..." }
];

const INTERVAL_UNITS = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" }
];

function humanReadableSchedule(config) {
  const { frequencyMode, frequency, relativeDays, relativeDirection, timeOfDay } = config;
  const time = timeOfDay ? ` at ${timeOfDay}` : "";
  if (frequencyMode === "relative") {
    if (relativeDays != null && relativeDirection) {
      return `${relativeDays} day${relativeDays !== 1 ? "s" : ""} ${relativeDirection} fill${time}`;
    }
    return `Relative${time}`;
  }
  // Handle custom interval format "every_X_unit"
  if (frequency?.startsWith("every_") && frequency !== "every_other_day") {
    const parts = frequency.split("_");
    return `Every ${parts[1]} ${parts[2]}${time}`;
  }
  const label = FREQUENCIES.find((f) => f.value === frequency)?.label || frequency || "Monthly";
  return `${label}${time}`;
}

const RefillReminderConfiguration = () => {
  const [configs, setConfigs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [patientSearchId, setPatientSearchId] = useState("");
  const [patientSettings, setPatientSettings] = useState(null);

  // Controlled form state for conditional field visibility
  const [reminderType, setReminderType] = useState("refill_reminder");
  const [frequencyMode, setFrequencyMode] = useState("fixed");
  const [customInterval, setCustomInterval] = useState({ value: 7, unit: "days" });

  // Default message from settings
  const [defaultMessage] = useGetSetting(["reminders.default-message"]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configsRes, brandsRes] = await Promise.all([
        getRefillReminderConfigurationsAPI(),
        getBrandJourneyConfigurationsAPI()
      ]);
      setConfigs(configsRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      message.error("Failed to load configurations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Derive drugName from the selected brand (brand IS the drug)
      const selectedBrand = brands.find((b) => b.id === values.brandJourneyConfiguration);
      const drugName = selectedBrand?.rxnormDrugName || selectedBrand?.brandName || "";

      const data = {
        brandJourneyConfiguration: values.brandJourneyConfiguration,
        drugName,
        type: values.type || "refill_reminder",
        message: values.message || null,
        frequencyMode: values.frequencyMode || "fixed",
        frequency:
          values.frequencyMode === "relative"
            ? null
            : values.frequency === "custom"
            ? `every_${customInterval.value}_${customInterval.unit}`
            : values.frequency,
        relativeDays: values.frequencyMode === "relative" ? values.relativeDays : null,
        relativeDirection: values.frequencyMode === "relative" ? values.relativeDirection : null,
        timeOfDay: values.timeOfDay ? values.timeOfDay.format("HH:mm") : "09:00",
        channels: {
          email: values.channelEmail || false,
          sms: values.channelSms || false,
          inApp: values.channelInApp || false
        },
        isEnabled: values.isEnabled !== false
      };

      if (values.type === "refill_reminder" && values.strengthDose) {
        data.strengthDose = values.strengthDose;
      }

      if (editingItem) {
        await updateRefillReminderConfigurationAPI(editingItem.id, data);
        message.success("Configuration updated");
      } else {
        await createRefillReminderConfigurationAPI(data);
        message.success("Configuration created");
      }
      setDrawerVisible(false);
      setEditingItem(null);
      form.resetFields();
      setReminderType("refill_reminder");
      setFrequencyMode("fixed");
      fetchData();
    } catch (error) {
      if (!error.errorFields) message.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    const type = record.type || "refill_reminder";
    const mode = record.frequencyMode || "fixed";
    setReminderType(type);
    setFrequencyMode(mode);
    // Parse custom interval format
    if (record.frequency?.startsWith("every_") && record.frequency !== "every_other_day") {
      const parts = record.frequency.split("_");
      setCustomInterval({ value: parseInt(parts[1]) || 7, unit: parts[2] || "days" });
    }
    form.setFieldsValue({
      ...record,
      type,
      frequencyMode: mode,
      frequency:
        record.frequency?.startsWith("every_") && record.frequency !== "every_other_day" ? "custom" : record.frequency,
      timeOfDay: record.timeOfDay ? moment(record.timeOfDay, "HH:mm") : null,
      channelEmail: record.channels?.email,
      channelSms: record.channels?.sms,
      channelInApp: record.channels?.inApp
    });
    setDrawerVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete reminder for "${record.drugName}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await deleteRefillReminderConfigurationAPI(record.id);
        message.success("Configuration deleted");
        fetchData();
      }
    });
  };

  const handlePatientSearch = async () => {
    if (!patientSearchId) return;
    try {
      const { data } = await getPatientRefillSettingsAPI(patientSearchId);
      setPatientSettings(data);
    } catch (error) {
      message.error("Failed to fetch patient settings");
      setPatientSettings(null);
    }
  };

  const handleOpenDrawer = () => {
    setEditingItem(null);
    setReminderType("refill_reminder");
    setFrequencyMode("fixed");
    form.resetFields();
    // Pre-fill message with default from settings
    if (defaultMessage) {
      form.setFieldsValue({ message: defaultMessage });
    }
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 140,
      render: (t) =>
        t === "message" ? (
          <Tag color="blue" icon={<MessageOutlined />}>
            Message
          </Tag>
        ) : (
          <Tag color="green" icon={<BellOutlined />}>
            Refill Reminder
          </Tag>
        )
    },
    { title: "Drug Name", dataIndex: "drugName", key: "drugName", width: 180 },
    {
      title: "Brand",
      dataIndex: "brandJourneyConfiguration",
      key: "brand",
      width: 120,
      render: (id) => brands.find((b) => b.id === id)?.brandName || "-"
    },
    {
      title: "Schedule",
      key: "schedule",
      width: 180,
      render: (_, record) => humanReadableSchedule(record)
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 200,
      render: (msg) =>
        msg ? (
          <Tooltip title={msg}>
            <Text ellipsis style={{ maxWidth: 180, display: "block" }}>
              {msg.length > 60 ? `${msg.substring(0, 60)}…` : msg}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">Default</Text>
        )
    },
    {
      title: "Channels",
      dataIndex: "channels",
      key: "channels",
      width: 160,
      render: (ch) => (
        <Space>
          {ch?.email && <Tag color="blue">Email</Tag>}
          {ch?.sms && <Tag color="green">SMS</Tag>}
          {ch?.inApp && <Tag color="purple">In-App</Tag>}
        </Space>
      )
    },
    {
      title: "Enabled",
      dataIndex: "isEnabled",
      key: "enabled",
      width: 80,
      render: (v) => <Switch checked={v} size="small" disabled />
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
        </Space>
      )
    }
  ];

  return (
    <PageContainer>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/settings">Settings</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Reminders</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0" }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Reminder Configuration
          </Title>
          <Text type="secondary">
            Configure default reminder settings per drug. Patients can customize these in TM Connect.
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenDrawer}>
          Add Reminder
        </Button>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        <Table
          columns={columns}
          dataSource={configs}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
        />
      </div>

      <Card title="Patient Reminder Settings Lookup" style={{ marginTop: 16 }} size="small">
        <Space>
          <Input
            placeholder="Enter Patient ID"
            value={patientSearchId}
            onChange={(e) => setPatientSearchId(e.target.value)}
            onPressEnter={handlePatientSearch}
            style={{ width: 200 }}
          />
          <Button icon={<SearchOutlined />} onClick={handlePatientSearch}>
            Search
          </Button>
        </Space>
        {patientSettings && (
          <div style={{ marginTop: 12 }}>
            {patientSettings.map((pref) => (
              <Descriptions key={pref.configId} bordered size="small" column={5} style={{ marginBottom: 8 }}>
                <Descriptions.Item label="Drug">{pref.drugName}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  {pref.type === "message" ? "Message" : "Refill Reminder"}
                </Descriptions.Item>
                <Descriptions.Item label="Schedule">{humanReadableSchedule(pref)}</Descriptions.Item>
                <Descriptions.Item label="Customized">
                  {pref.hasCustomization ? <Tag color="orange">Patient override</Tag> : "Default"}
                </Descriptions.Item>
                <Descriptions.Item label="Enabled">{pref.isEnabled ? "Yes" : "No"}</Descriptions.Item>
              </Descriptions>
            ))}
          </div>
        )}
      </Card>

      <Drawer
        title={editingItem ? "Edit Reminder" : "Add Reminder"}
        width={520}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setEditingItem(null);
        }}
        destroyOnClose
        footer={
          <Space style={{ float: "right" }}>
            <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {/* Type selector */}
          <Form.Item name="type" label="Reminder Type" initialValue="refill_reminder">
            <Radio.Group onChange={(e) => setReminderType(e.target.value)} buttonStyle="solid">
              <Radio.Button value="refill_reminder">
                <BellOutlined /> Refill Reminder
              </Radio.Button>
              <Radio.Button value="message">
                <MessageOutlined /> Generic Message
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Brand — drug name is derived automatically from the brand */}
          <Form.Item name="brandJourneyConfiguration" label="Brand" rules={[{ required: true }]}>
            <Select placeholder="Select brand">
              {brands.map((b) => (
                <Option key={b.id} value={b.id}>
                  {b.brandName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Strength/Dose — only for refill_reminder */}
          {reminderType === "refill_reminder" && (
            <Form.Item name="strengthDose" label="Default Strength / Dose">
              <Input placeholder="e.g., 150mg every 4 weeks" />
            </Form.Item>
          )}

          {/* Message field */}
          <Form.Item
            name="message"
            label="Message"
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Tokens: <code>{"{name}"}</code> patient name · <code>{"{drug}"}</code> drug name ·{" "}
                <code>{"{link}"}</code> secure link
              </Text>
            }
          >
            <TextArea
              rows={3}
              placeholder={`Hi {name}, your ${
                reminderType === "refill_reminder" ? "{drug} refill" : "message"
              } is coming up...`}
            />
          </Form.Item>

          {/* Frequency Mode */}
          <Form.Item name="frequencyMode" label="Schedule Type" initialValue="fixed">
            <Segmented
              options={[
                { label: "Fixed Schedule", value: "fixed" },
                { label: "Relative to Fill Date", value: "relative" }
              ]}
              onChange={setFrequencyMode}
            />
          </Form.Item>

          {frequencyMode === "fixed" ? (
            <>
              <Form.Item name="frequency" label="Frequency" initialValue="monthly">
                <Select options={FREQUENCIES} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.frequency !== cur.frequency}>
                {() => {
                  const freq = form.getFieldValue("frequency");
                  if (freq !== "custom") return null;
                  return (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
                      <span>Every</span>
                      <InputNumber
                        min={1}
                        max={365}
                        value={customInterval.value}
                        onChange={(val) => setCustomInterval((prev) => ({ ...prev, value: val || 1 }))}
                        style={{ width: 80 }}
                      />
                      <Select
                        value={customInterval.unit}
                        options={INTERVAL_UNITS}
                        onChange={(val) => setCustomInterval((prev) => ({ ...prev, unit: val }))}
                        style={{ width: 100 }}
                      />
                    </div>
                  );
                }}
              </Form.Item>
            </>
          ) : (
            <Form.Item label="Relative Schedule">
              <Space>
                <Form.Item
                  name="relativeDays"
                  noStyle
                  rules={[{ required: frequencyMode === "relative", message: "Enter number of days" }]}
                >
                  <InputNumber min={1} max={365} placeholder="Days" style={{ width: 90 }} />
                </Form.Item>
                <Text>days</Text>
                <Form.Item
                  name="relativeDirection"
                  noStyle
                  rules={[{ required: frequencyMode === "relative", message: "Select direction" }]}
                >
                  <Select style={{ width: 100 }}>
                    <Option value="before">Before</Option>
                    <Option value="after">After</Option>
                  </Select>
                </Form.Item>
                <Text>next fill date</Text>
              </Space>
            </Form.Item>
          )}

          {/* Time of Day — always shown */}
          <Form.Item name="timeOfDay" label="Time of Day">
            <TimePicker format="HH:mm" minuteStep={15} />
          </Form.Item>

          {/* Channels */}
          <Form.Item label="Notification Channels">
            <Space>
              <Form.Item name="channelEmail" valuePropName="checked" noStyle initialValue={true}>
                <Checkbox>Email</Checkbox>
              </Form.Item>
              <Form.Item name="channelSms" valuePropName="checked" noStyle initialValue={true}>
                <Checkbox>SMS</Checkbox>
              </Form.Item>
              <Form.Item name="channelInApp" valuePropName="checked" noStyle initialValue={true}>
                <Checkbox>In-App Chat</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>

          {/* Enabled toggle */}
          <Form.Item name="isEnabled" label="Enabled" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </PageContainer>
  );
};

export { RefillReminderConfiguration };
